import { Fragment } from 'react';
import GuestStyleCell from './GuestStyleCell';
import { planGridRows } from '../../utils/availabilitySnapshotUtil';
import '../../css/AvailabilityGuestGrid.css';

/**
 * The hours × courts table of the full-grid images, in the guest booking
 * page's design: court names with "(indoor)" over each column, a time column
 * of "3:00 – 4:00" over "PM  PM", and the guest page's slot cells. Shared by
 * the dark and light templates at every size.
 *
 * Rows are one per hour when that stays readable (or always, with
 * rowMode 'hourly'), otherwise hours with the same availability on every
 * court are merged into ranges (see planGridRows).
 *
 * @param {object} props
 * @param {object} props.snapshot - buildAvailabilitySnapshot() output
 * @param {number} props.bodyHeight - px the hour rows may take in total
 * @param {number} props.minHourlyUnit - smallest scale (1 = guest-page size) at which hourly rows are used
 * @param {number} props.maxUnit - largest scale the type may grow to
 * @param {number} props.maxRowHeight - px; rows stop growing here on a short day
 * @param {number} props.tableWidth - px across the whole table
 * @param {number} props.maxHeaderUnit - largest scale for the court header row
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour; 'ranges' merges hours when hourly rows would be too small
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours); see availabilityImageSettingsUtil
 * @returns {JSX.Element}
 */
function GuestStyleGridTable({ snapshot, bodyHeight, minHourlyUnit, maxUnit, maxRowHeight, tableWidth, maxHeaderUnit, rowMode, imageSettings }) {
  const { courts } = snapshot;
  const {
    rows, merged, rowHeight, unit, cellUnit, headerUnit, cellTextBoost, openPlayTextBoost, timeTextBoost, nextDayTimeTextBoost,
  } = planGridRows(
    snapshot,
    {
      bodyHeight, minHourlyUnit, maxUnit, maxRowHeight, tableWidth, maxHeaderUnit, rowMode,
      textPx: { time: imageSettings?.timePx, cell: imageSettings?.cellPx },
    },
  );
  // BOOKED cell colours from the image settings; the CSS falls back to the
  // guest grid's grey when they're absent.
  const tableStyle = {
    '--agg-booked-bg': imageSettings?.bookedBg || undefined,
    '--agg-booked-ink': imageSettings?.bookedText || undefined,
  };
  const columns = `max-content repeat(${Math.max(courts.length, 1)}, minmax(0, 1fr))`;

  // Header and rows share ONE grid so the court columns line up with the
  // slot cells: with two separate grids the `max-content` time column was
  // sized by the empty header spacer (0px) in one and by the time labels in
  // the other, shifting every court name left of its column.
  return (
    <div className={`agg-table${merged ? ' agg-table-merged' : ''}`} style={tableStyle}>
      <div
        className="agg-body"
        style={{
          gridTemplateColumns: columns, gridTemplateRows: 'auto', gridAutoRows: `${rowHeight}px`,
          '--u': unit, '--uc': cellUnit, '--tb': cellTextBoost, '--tbo': openPlayTextBoost, '--tt': timeTextBoost,
        }}
      >
        <span className="agg-head-spacer" />
        {courts.map(court => (
          <span key={court.number} className="agg-court-header" style={{ '--u': headerUnit }}>
            <span className="agg-court-name">{court.label}</span>
            {court.envLabel && <span className="agg-court-env">({court.envLabel})</span>}
            {court.rateLabel && <span className="agg-court-rate">{court.rateLabel}</span>}
          </span>
        ))}

        {rows.map(row => (
          <Fragment key={row.key}>
            <span className="agg-time-cell" style={row.isNextDay ? { '--tt': nextDayTimeTextBoost } : undefined}>
              <span className="agg-time-h">{row.timeParts.start}</span>
              <span className="agg-time-dash">{'–'}</span>
              <span className="agg-time-h">{row.timeParts.end}</span>
              <span className="agg-time-meridiem agg-time-meridiem-start">{row.timeParts.startMeridiem}</span>
              <span className="agg-time-meridiem agg-time-meridiem-end">{row.timeParts.endMeridiem}</span>
              {row.isNextDay && <span className="agg-time-nextday">{snapshot.nextDayLabel}</span>}
            </span>
            {courts.map(court => (
              <div key={court.number} className="agg-cell-wrap">
                <GuestStyleCell cell={row.cells[court.number]} />
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export default GuestStyleGridTable;
